import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui';

/*
 * ClientLogoCarousel
 * ------------------
 * Infinite right-to-left marquee of client logos. Two copies of the
 * logo list are rendered end-to-end and the flex container animates
 * by -50%, which places the second copy exactly where the first
 * started at animation end — seamless loop, no flicker on restart.
 *
 * Every tile is a real link to the company's website (opens in a
 * new tab). Each logo has a four-CDN fallback chain (logo.dev →
 * Clearbit → Google favicon → DuckDuckGo icons) with a graceful
 * text-name fallback when everything else 404s, so the marquee
 * never renders a broken image.
 *
 * The `scroll` keyframe is defined at document level in
 * `src/index.css`, so the inline `animation: scroll ...` works
 * anywhere this component is dropped.
 *
 * Shared component used by /about ("We have worked with") and by
 * the homepage ("Trusted by teams shipping the work"). Editing the
 * logo list or a tile's styling in one place updates both surfaces.
 */

// Client list — Clearbit-style logo CDNs resolve these domains to
// PNG logos in most cases. Keep names + domains in sync when new
// companies are added or renamed.
const CLIENT_LOGOS = [
  { name: 'Kimberly-Clark (KCC)', domain: 'kimberly-clark.com' },
  { name: 'Moglix', domain: 'moglix.com' },
  { name: 'LabCorp', domain: 'labcorp.com' },
  { name: 'MobiKwik', domain: 'mobikwik.com' },
  { name: 'TCS', domain: 'tcs.com' },
  { name: 'Infosys', domain: 'infosys.com' },
  { name: 'Salesforce', domain: 'salesforce.com' },
  { name: 'Accenture', domain: 'accenture.com' },
  { name: 'SimplifyVMS', domain: 'simplifyvms.com' },
] as const;

export interface ClientLogoCarouselProps {
  /** Section heading. Defaults to "We have worked with". */
  heading?: string;
  /** Optional subheading rendered below the main heading. */
  subheading?: string;
  /** Set to false to skip the top Container heading block and only
   *  render the marquee itself. */
  showHeader?: boolean;
}

export const ClientLogoCarousel: React.FC<ClientLogoCarouselProps> = ({
  heading = 'We have worked with',
  subheading = 'Organizations of every size that have trusted us with their technology.',
  showHeader = true,
}) => (
  <section className="bg-white py-16 md:py-20 lg:py-24 overflow-hidden">
    {showHeader && (
      <Container>
        <div className="text-center mb-10 md:mb-14">
          <motion.h2
            className="heading-lg text-navy-900 mb-3"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {heading}
          </motion.h2>
          {subheading && (
            <motion.p
              className="text-base md:text-lg text-navy-600"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{
                duration: 0.4,
                delay: 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {subheading}
            </motion.p>
          )}
        </div>
      </Container>
    )}

    {/* Marquee track — full-bleed for continuous scroll. */}
    <div className="relative">
      <div
        className="flex gap-14 md:gap-20 whitespace-nowrap"
        style={{
          animation: 'scroll 40s linear infinite',
          width: 'max-content',
        }}
      >
        {/* Duplicated set so translating -50% lands the copy exactly
            where the original started — no visible reset flicker. */}
        {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
          <LogoTile key={i} logo={logo} />
        ))}
      </div>
      {/* Edge fades so the marquee appears to slide "out of" the
          section rather than getting cropped mid-motion. */}
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-20 md:w-32 bg-gradient-to-r from-white to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-20 md:w-32 bg-gradient-to-l from-white to-transparent"
        aria-hidden
      />
    </div>
  </section>
);

const LogoTile: React.FC<{ logo: { name: string; domain: string } }> = ({
  logo,
}) => {
  const sources = useMemo(
    () => [
      `https://img.logo.dev/${logo.domain}?token=pk_X-1ZO13GSgeOoUrIuJ6GMQ&size=256&format=png`,
      `https://logo.clearbit.com/${logo.domain}?size=256`,
      `https://www.google.com/s2/favicons?domain=${logo.domain}&sz=256`,
      `https://icons.duckduckgo.com/ip3/${logo.domain}.ico`,
    ],
    [logo.domain],
  );

  const attemptRef = useRef(0);
  const [currentSrc, setCurrentSrc] = useState(sources[0]);
  const [showText, setShowText] = useState(false);

  const handleError = () => {
    const nextIndex = attemptRef.current + 1;
    if (nextIndex >= sources.length) {
      setShowText(true);
      return;
    }
    attemptRef.current = nextIndex;
    setCurrentSrc(sources[nextIndex]);
  };

  return (
    <a
      href={`https://${logo.domain}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Visit ${logo.name}'s website (opens in a new tab)`}
      className="flex-shrink-0 flex items-center justify-center h-16 md:h-20 w-40 md:w-52 px-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 rounded-lg transition-transform hover:-translate-y-0.5"
    >
      {showText ? (
        <span className="text-navy-700 font-semibold text-base md:text-lg tracking-wide text-center group-hover:text-accent-600 transition-colors">
          {logo.name}
        </span>
      ) : (
        <img
          src={currentSrc}
          alt={logo.name}
          className="max-h-full max-w-full object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
          loading="lazy"
          onError={handleError}
        />
      )}
    </a>
  );
};
